import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { transactionApi } from '@/lib/api';

export const merchantSuggestionKeys = {
  all: ['merchants', 'suggest'] as const,
  query: (q: string) => [...merchantSuggestionKeys.all, q] as const,
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Debounced merchant prefix suggestions (min 2 chars, 300ms debounce).
 */
export function useMerchantSuggestions(input: string) {
  const trimmed = input.trim();
  const debouncedQuery = useDebouncedValue(trimmed, 300);

  return useQuery({
    queryKey: merchantSuggestionKeys.query(debouncedQuery),
    queryFn: () => transactionApi.suggestMerchants(debouncedQuery, 10),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
